import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { getJobCustomer, getDisplayStatus, statusColors, statusLabels } from '../../utils/reportHelpers';

export default function DrilldownDialog({ open, onOpenChange, title, description, jobs, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="overflow-y-auto dialog-resizable"
        style={{ width: '64rem', maxHeight: '90vh' }}
        data-testid="report-drilldown-dialog"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Scheduled Date</TableHead>
                  <TableHead>Completion Date</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Auditor(s)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => {
                  const customer = getJobCustomer(job);
                  const scheduledDateDisplay = job.end_date && job.end_date !== job.scheduled_date
                    ? `${format(parseISO(job.scheduled_date), 'd MMM')} - ${format(parseISO(job.end_date), 'd MMM yyyy')}`
                    : format(parseISO(job.scheduled_date), 'd MMM yyyy');
                  const completionDate = job.completed_on || job.completed_at;
                  const completionDateDisplay = job.status === 'completed' && completionDate
                    ? format(parseISO(completionDate), 'd MMM yyyy')
                    : '\u2014';
                  return (
                    <TableRow key={job.id}>
                      <TableCell className="font-mono text-sm">{job.id}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {scheduledDateDisplay}
                      </TableCell>
                      <TableCell>
                        {completionDateDisplay}
                      </TableCell>
                      <TableCell>
                        {job.days_to_complete !== null && job.days_to_complete !== undefined ? (
                          <span className={job.days_to_complete <= 1 ? 'text-green-600 font-medium' : ''}>
                            {job.days_to_complete}
                          </span>
                        ) : '\u2014'}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[getDisplayStatus(job)] || 'bg-gray-100'}>
                          {statusLabels[getDisplayStatus(job)] || job.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {customer?.colour && (
                            <div 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: customer.colour }}
                            />
                          )}
                          {customer?.name || '\u2014'}
                        </div>
                      </TableCell>
                      <TableCell>{job.site?.name || '\u2014'}</TableCell>
                      <TableCell>{job.service?.name || '\u2014'}</TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        {job.auditors?.map(a => `${a.first_name} ${a.last_name[0]}.`).join(', ') || '\u2014'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
        
        <div className="pt-4 border-t text-sm text-muted-foreground">
          Showing {jobs.length} job{jobs.length !== 1 ? 's' : ''}
        </div>
      </DialogContent>
    </Dialog>
  );
}
